// Surface 3 · Activation modal (/frameworks/{frameworkId}/activate).
//
// Spec: specs/consumer-frameworks-strings.md § Surface 3 · Activation.
//
// Fetches the framework detail, lays out the activation form (name,
// start date, per-activity duration overrides, single acknowledgement),
// submits to POST /me/frameworks/{id}/activate, and on success routes
// the tenant straight into the workspace Focus tab (MR 5).
//
// Design choices reflected (handoff answers to open questions):
//   - Q3 "Activation gate on stakeholder/document review?"
//     → SINGLE checkbox, not per-item. Three highest-seniority stakeholders
//       named inline (today: hardcoded Board/CEO/CFO — spec says they
//       should be derived from the framework's stakeholder list; wiring
//       that lands when stakeholder-seniority metadata exists upstream).
//   - Q1 "Duration = critical path, not sum of activity durations"
//     → helper line under the duration grid.
//   - Only activities with `tenant_override_allowed: true` render the
//     duration <select>; others are read-only labels.
//
// Routing: rendered by App.tsx at /frameworks/:frameworkId/activate.
// Cancel → back to /frameworks/{id}. Submit success → /frameworks/instances/{newId}.
// Already-running case: if the framework has an active instance for this
// tenant, we show a smaller confirm with "Open your running instance /
// Cancel" instead of the full form.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Modal } from '@gc-coach/design-system';
import type {
  Activity,
  ActivateFrameworkRequest,
  DurationBucket,
  DurationOverride,
  GetMeFrameworkDetailResponse,
} from '@gc-coach/shared';
import { activateFramework, getMeFrameworkDetail } from '../api/endpoints.js';
import { useI18n } from '../i18n/index.js';
import styles from './ActivationModal.module.css';

// Midpoint months for each bucket — used when we need a concrete number to
// send to the backend. Matches the lower-bound/upper-bound ranges in
// shared's DurationBucket. Midpoint keeps planning honest (not optimistic,
// not pessimistic).
const BUCKET_MIDPOINT_MONTHS: Record<DurationBucket, number> = {
  lt_1_month: 0.5,
  '1_3_months': 2,
  '3_6_months': 4.5,
  '6_9_months': 7.5,
  '9_12_months': 10.5,
  '12_18_months': 15,
  '18_24_months': 21,
};

const SELECTABLE_BUCKETS: ReadonlyArray<DurationBucket> = [
  'lt_1_month',
  '1_3_months',
  '3_6_months',
  '6_9_months',
  '9_12_months',
  '12_18_months',
  '18_24_months',
] as const;

type FlatActivity = {
  activity: Activity;
  phaseIndex: number;
};

export function ActivationModal() {
  const { frameworkId } = useParams<{ frameworkId: string }>();
  const navigate = useNavigate();
  const { t, tt } = useI18n();

  const [fetchState, setFetchState] = useState<
    | { kind: 'loading' }
    | { kind: 'error'; message: string }
    | { kind: 'notfound' }
    | { kind: 'ok'; data: GetMeFrameworkDetailResponse }
  >({ kind: 'loading' });

  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState(todayIso());
  const [acknowledged, setAcknowledged] = useState(false);
  // Keyed by activity_id → bucket. Omitted entries = use the framework default.
  const [overrides, setOverrides] = useState<Map<string, DurationBucket>>(new Map());
  const [submitState, setSubmitState] = useState<
    { kind: 'idle' } | { kind: 'submitting' } | { kind: 'error'; message: string }
  >({ kind: 'idle' });

  useEffect(() => {
    if (!frameworkId) {
      setFetchState({ kind: 'notfound' });
      return;
    }
    let cancelled = false;
    (async () => {
      const result = await getMeFrameworkDetail(frameworkId);
      if (cancelled) return;
      if (!result.ok) {
        if (result.code === 'NOT_FOUND') {
          setFetchState({ kind: 'notfound' });
        } else {
          setFetchState({ kind: 'error', message: t('frameworks.library.errorGeneric') });
        }
        return;
      }
      setFetchState({ kind: 'ok', data: result.data });
      setName(result.data.framework.title);
    })();
    return () => {
      cancelled = true;
    };
  }, [frameworkId, t]);

  const flatActivities: ReadonlyArray<FlatActivity> = useMemo(() => {
    if (fetchState.kind !== 'ok') return [];
    const out: FlatActivity[] = [];
    fetchState.data.framework.phases.forEach((phase, pi) => {
      for (const a of phase.activities) {
        out.push({ activity: a, phaseIndex: pi + 1 });
      }
    });
    return out;
  }, [fetchState]);

  const onClose = () => {
    if (frameworkId) navigate(`/frameworks/${frameworkId}`);
    else navigate('/frameworks');
  };

  const onSubmit = async () => {
    if (fetchState.kind !== 'ok' || !frameworkId) return;
    if (!acknowledged || submitState.kind === 'submitting') return;
    setSubmitState({ kind: 'submitting' });

    const body: ActivateFrameworkRequest = {
      name: name.trim(),
      start_date: startDate,
      stakeholder_acknowledgement: true,
      duration_overrides: buildDurationOverrides(overrides),
    };

    const result = await activateFramework(frameworkId, body);
    if (!result.ok) {
      setSubmitState({
        kind: 'error',
        message:
          result.code === 'BAD_REQUEST' && result.message
            ? result.message
            : t('frameworks.activation.errorGeneric'),
      });
      return;
    }
    navigate(`/frameworks/instances/${result.data.instance.id}`);
  };

  // ─── Loading / not-found / error states ────────────────────────────────
  if (fetchState.kind === 'loading') {
    return (
      <Modal open title="" onClose={onClose} width={560}>
        <div className={styles.loadingBody}>…</div>
      </Modal>
    );
  }
  if (fetchState.kind === 'notfound') {
    return (
      <Modal open title={t('frameworks.preview.notFoundTitle')} onClose={onClose} width={420}>
        <p className={styles.body}>{t('frameworks.preview.notFoundBody')}</p>
      </Modal>
    );
  }
  if (fetchState.kind === 'error') {
    return (
      <Modal open title={t('frameworks.activation.errorTitle')} onClose={onClose} width={420}>
        <p className={styles.body}>{fetchState.message}</p>
      </Modal>
    );
  }

  const { framework, active_instance_id } = fetchState.data;

  // ─── Already-running confirm (replaces the full form) ──────────────────
  if (active_instance_id) {
    return (
      <Modal
        open
        title={t('frameworks.activation.alreadyRunningTitle')}
        onClose={onClose}
        width={420}
        footer={
          <div className={styles.footerRow}>
            <button type="button" className={styles.ghostBtn} onClick={onClose}>
              {t('frameworks.activation.cancel')}
            </button>
            <span style={{ flex: 1 }} />
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={() => navigate(`/frameworks/instances/${active_instance_id}`)}
            >
              {t('frameworks.activation.alreadyRunningContinue')} →
            </button>
          </div>
        }
      >
        <p className={styles.body}>{t('frameworks.activation.alreadyRunningBody')}</p>
      </Modal>
    );
  }

  // ─── Full activation form ──────────────────────────────────────────────
  const deliverableNames = (framework.deliverables ?? []).map((d) => d.name).join(' · ');
  const target = framework.maturity_target?.level ?? null;

  const canSubmit =
    acknowledged &&
    name.trim().length > 0 &&
    /^\d{4}-\d{2}-\d{2}$/.test(startDate) &&
    submitState.kind !== 'submitting';

  return (
    <Modal
      open
      onClose={onClose}
      width={640}
      title={
        <div>
          <div className={styles.eyebrow}>{t('frameworks.activation.stepLabel')}</div>
          <div className={styles.title}>
            {tt('frameworks.activation.titleTemplate', { name: framework.title })}
          </div>
        </div>
      }
      footer={
        <div className={styles.footerRow}>
          <button
            type="button"
            className={styles.ghostBtn}
            onClick={onClose}
            disabled={submitState.kind === 'submitting'}
          >
            {t('frameworks.activation.cancel')}
          </button>
          <span style={{ flex: 1 }} />
          <button
            type="button"
            className={styles.primaryBtn}
            disabled={!canSubmit}
            onClick={onSubmit}
          >
            {submitState.kind === 'submitting'
              ? t('frameworks.activation.submitting')
              : `${t('frameworks.activation.submit')} →`}
          </button>
        </div>
      }
    >
      {/* Summary rows */}
      <div className={styles.kvGrid}>
        {deliverableNames && (
          <>
            <span className={styles.k}>{t('frameworks.activation.summaryDeliverables')}</span>
            <span className={styles.v}>{deliverableNames}</span>
          </>
        )}
        {target && (
          <>
            <span className={styles.k}>{t('frameworks.activation.summaryMaturity')}</span>
            <span className={styles.v}>
              {t(`frameworks.domain.${framework.domain}`)} →{' '}
              <strong>{t(`frameworks.maturity.${target}`)}</strong>
            </span>
          </>
        )}
        {framework.estimated_effort_label && (
          <>
            <span className={styles.k}>{t('frameworks.activation.summaryDuration')}</span>
            <span className={styles.v}>{framework.estimated_effort_label}</span>
          </>
        )}
      </div>

      {/* Instance name */}
      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor="instance-name">
          {t('frameworks.activation.nameLabel')}
        </label>
        <input
          id="instance-name"
          type="text"
          className={styles.input}
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          disabled={submitState.kind === 'submitting'}
        />
      </div>

      {/* Start date */}
      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor="start-date">
          {t('frameworks.activation.startDateLabel')}
        </label>
        <input
          id="start-date"
          type="date"
          className={styles.input}
          value={startDate}
          onChange={(e) => setStartDate(e.currentTarget.value)}
          disabled={submitState.kind === 'submitting'}
        />
        <div className={styles.note}>{t('frameworks.activation.startDateNote')}</div>
      </div>

      {/* Duration overrides */}
      {flatActivities.length > 0 && (
        <div className={styles.field}>
          <div className={styles.fieldLabel}>{t('frameworks.activation.adjustDurationsLabel')}</div>
          <div className={styles.durationGrid}>
            {flatActivities.map((fa, i) => {
              const defaultBucket = fa.activity.duration?.default_months ?? null;
              const overrideAllowed = fa.activity.duration?.tenant_override_allowed ?? false;
              const selected = overrides.get(fa.activity.id) ?? defaultBucket;
              return (
                <div
                  key={fa.activity.id}
                  className={styles.durationRow}
                  style={i < flatActivities.length - 1 ? undefined : { borderBottom: 'none' }}
                >
                  <span className={styles.durationPhase}>P{fa.phaseIndex}</span>
                  <span className={styles.durationName}>{fa.activity.title}</span>
                  {overrideAllowed && defaultBucket ? (
                    <select
                      className={styles.durationSelect}
                      value={selected ?? ''}
                      onChange={(e) => {
                        const next = new Map(overrides);
                        const value = e.currentTarget.value as DurationBucket;
                        if (value === defaultBucket) next.delete(fa.activity.id);
                        else next.set(fa.activity.id, value);
                        setOverrides(next);
                      }}
                      disabled={submitState.kind === 'submitting'}
                    >
                      {SELECTABLE_BUCKETS.map((b) => (
                        <option key={b} value={b}>
                          {t(`frameworks.duration.${b}`)}
                          {b === defaultBucket ? ` · ${t('frameworks.activation.defaultTag')}` : ''}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className={styles.durationStatic}>
                      {defaultBucket
                        ? t(`frameworks.duration.${defaultBucket}`)
                        : t('frameworks.preview.timeUnknown')}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          <div className={styles.note}>{t('frameworks.activation.adjustDurationsNote')}</div>
          <div className={styles.criticalPathNote}>
            {t('frameworks.activation.criticalPathNote')}
          </div>
        </div>
      )}

      {/* Acknowledgement */}
      <div className={styles.ackRow}>
        <input
          id="ack"
          type="checkbox"
          checked={acknowledged}
          onChange={(e) => setAcknowledged(e.currentTarget.checked)}
          disabled={submitState.kind === 'submitting'}
        />
        <label htmlFor="ack" className={styles.ackLabel}>
          {t('frameworks.activation.ackLabel')}
        </label>
      </div>

      {submitState.kind === 'error' && (
        <div className={styles.errorBanner}>{submitState.message}</div>
      )}
    </Modal>
  );
}

function todayIso(): string {
  const d = new Date();
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function buildDurationOverrides(
  overrides: Map<string, DurationBucket>,
): ReadonlyArray<DurationOverride> {
  const out: DurationOverride[] = [];
  overrides.forEach((bucket, activity_id) => {
    out.push({ activity_id, months: BUCKET_MIDPOINT_MONTHS[bucket] });
  });
  return out;
}
